import { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { Modal, Button, Radio, Slider } from 'antd';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import { UploadIcon } from 'src/icons';
import { showToastError } from 'src/utilities/errortoast';

const UploadImages = ({
  updateLogoUrl,
  selectedFile,
  setSelectedFile,
  handleBlobSelect,
  isEditPage,
  setOrgData,
  orgData,
  logoUrl,
  fromAddPage,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [urlSubmitted, setUrlSubmitted] = useState(false);

  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const cropperRef = useRef(null);
  const baseZoomRef = useRef(1);
  const [isCropperReady, setIsCropperReady] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(0);

  const fileInputRef = useRef(null);
  const urlInputRef = useRef(null);

  useEffect(() => {
    if (isEditPage && orgData?.logo_url) {
      setShowUrlInput(true);
    }
  }, [isEditPage, orgData?.logo_url]);

  useEffect(() => {
    if (logoUrl) {
      setImageUrl(logoUrl);
      setUrlSubmitted(true);
    }
  }, [logoUrl]);

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = [...e.dataTransfer.files];
    handleFileSelect(files[0]);
  };

  const handleFileInputChange = (e) => {
    const files = [...e.target.files];
    handleFileSelect(files[0]);
    e.target.value = null;
  };

  const handleFileSelect = (file) => {
    if (file) {
      const validTypes = ['image/png', 'image/jpeg'];
      const maxSize = 5 * 1024 * 1024;

      if (!validTypes.includes(file.type)) {
        showToastError('Only PNG and JPEG images are allowed.');
        return;
      }

      if (file.size > maxSize) {
        showToastError('File size should be maximum 5MB.');
        return;
      }

      if (imageToCrop) {
        URL.revokeObjectURL(imageToCrop);
      }

      const url = URL.createObjectURL(file);
      setImageToCrop(url);
      setCropModalOpen(true);
    }
  };

  const handleCropFinish = async () => {
    try {
      const cropper = cropperRef.current?.cropper;
      if (!cropper) {
        showToastError('Cropper is not ready yet. Please try again.');
        return;
      }

      const canvas = cropper.getCroppedCanvas();
      if (!canvas) {
        showToastError('Unable to crop image. Please try again.');
        return;
      }

      const mimeType = 'image/png';
      const quality = undefined;

      const croppedBlob = await new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), mimeType, quality);
      });

      if (!croppedBlob) {
        showToastError('Unable to export cropped image. Please try again.');
        return;
      }

      const ext = croppedBlob.type === 'image/png' ? 'png' : 'jpg';
      croppedBlob.ext = ext;

      handleBlobSelect(croppedBlob);

      URL.revokeObjectURL(imageToCrop);
      setImageToCrop(null);
      setCropModalOpen(false);
      setIsCropperReady(false);
      setRotation(0);
      setZoom(0);
      setAspectRatio(null);
    } catch (error) {
      showToastError('Error cropping image. Please try again.');
    }
  };

  const handleCropCancel = () => {
    URL.revokeObjectURL(imageToCrop);
    setImageToCrop(null);
    setCropModalOpen(false);
    setIsCropperReady(false);
    setRotation(0);
    setZoom(0);
    setAspectRatio(null);
  };

  const handleUploadButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
      fileInputRef.current.click();
    }
  };

  const handleChangeImageClick = () => {
    setOrgData((prevOrgData) => ({
      ...prevOrgData,
      logo_url: null,
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
      fileInputRef.current.click();
    }
  };

  const handleDelete = () => {
    setSelectedFile(null);
    setOrgData((prevOrgData) => ({
      ...prevOrgData,
      logo_url: null,
    }));
    fileInputRef.current.value = null;
  };

  const handleUploadAsUrlClick = () => {
    setShowUrlInput(true);
  };

  const handleUrlSubmit = () => {
    if (urlInputRef.current) {
      const enteredUrl = urlInputRef.current.value.trim();
      const validUrlExtensions = /\.(jpeg|jpg|png)$/i;
      if (enteredUrl === '') {
        showToastError('Please enter a URL.');
      } else if (
        !enteredUrl.startsWith('http://') &&
        !enteredUrl.startsWith('https://')
      ) {
        showToastError(
          'Please enter a valid URL starting with http:// or https://'
        );
      } else if (!validUrlExtensions.test(enteredUrl)) {
        showToastError(
          'Only direct image URLs are supported (PNG/JPEG). Please use the direct image URL, not a webpage URL. Right-click the image and select "Copy image address" to get the direct URL.'
        );
      } else {
        setImageUrl(enteredUrl);
        setUrlSubmitted(true);
        if (updateLogoUrl) {
          updateLogoUrl(enteredUrl);
        }
      }
    }
  };

  const handleRemoveUrl = () => {
    updateLogoUrl(null);
    setImageUrl('');
    if (urlInputRef.current) {
      urlInputRef.current.value = '';
    }
    if (updateLogoUrl) {
      updateLogoUrl(null);
    }
    setUrlSubmitted(false);
    setShowUrlInput(false);
  };

  useEffect(() => {
    const handlePaste = (event) => {
      const pastedText = (event.clipboardData || window.clipboardData).getData(
        'text'
      );
      const validUrlExtensions = /\.(jpeg|jpg|png)$/i;
      if (
        (pastedText.startsWith('http://') ||
          pastedText.startsWith('https://')) &&
        validUrlExtensions.test(pastedText)
      ) {
        setImageUrl(pastedText);
        setUrlSubmitted(true);
        if (updateLogoUrl) {
          updateLogoUrl(pastedText);
        }
      } else if (
        pastedText.startsWith('http://') ||
        pastedText.startsWith('https://')
      ) {
        showToastError(
          'Only direct image URLs are supported (PNG/JPEG). Please use the direct image URL, not a webpage URL. Right-click the image and select "Copy image address" to get the direct URL.'
        );
      }
    };

    if (urlInputRef.current) {
      urlInputRef.current.addEventListener('paste', handlePaste);
    }

    return () => {
      if (urlInputRef.current) {
        urlInputRef.current.removeEventListener('paste', handlePaste);
      }
    };
  }, [urlInputRef, updateLogoUrl]);

  const handleURLentry = (e) => {
    fromAddPage &&
      setOrgData((prevOrgData) => ({
        ...prevOrgData,
        logo_url: null,
      }));
    setImageUrl(e.target.value);
  };

  const handleRemoveOrCancel = () => {
    if (urlSubmitted || logoUrl) {
      handleRemoveUrl();
    } else {
      setImageUrl('');
      setShowUrlInput(false);
    }
  };

  const handleAspectRatioChange = (ratio) => {
    setAspectRatio(ratio);
  };

  useEffect(() => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper || !cropModalOpen || !isCropperReady) return;
    cropper.setAspectRatio(
      aspectRatio === null || aspectRatio === undefined ? NaN : aspectRatio
    );
  }, [aspectRatio, cropModalOpen, isCropperReady]);

  useEffect(() => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper || !cropModalOpen || !isCropperReady) return;
    cropper.rotateTo(rotation);
  }, [rotation, cropModalOpen, isCropperReady]);

  useEffect(() => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper || !cropModalOpen || !isCropperReady) return;
    const zoomRatio = baseZoomRef.current * (1 + zoom / 100);
    cropper.zoomTo(zoomRatio);
  }, [zoom, cropModalOpen, isCropperReady]);

  return (
    <>
      <div
        className={classNames(
          'h-[36rem] text-center bg-[#00000005] border-2 border-[#00d090] border-dashed rounded-lg p-6 relative',
          {
            'border-blue-500 bg-blue-100': isDragging,
          }
        )}
        onDragOver={(e) => e.preventDefault()}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileInputChange}
          accept="image/png, image/jpeg"
        />
        {selectedFile || (orgData?.logo_url && fromAddPage) ? (
          <>
            <img
              src={
                selectedFile
                  ? URL.createObjectURL(selectedFile)
                  : orgData?.logo_url
              }
              alt="Selected"
              className="mx-auto mb-4"
              style={{
                maxWidth: '400px',
                maxHeight: '150px',
                objectFit: 'contain',
              }}
            />
            <div className="flex justify-center">
              <button
                className="bg-[#00d090] text-white py-2 px-4 rounded mr-4"
                onClick={handleChangeImageClick}
              >
                Change Image
              </button>
              <button
                className="bg-red-500 text-white py-2 px-4 rounded"
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          </>
        ) : showUrlInput ? (
          <div className="flex flex-col items-center">
            {imageUrl && (
              <img
                src={imageUrl}
                className="mb-4"
                style={{
                  maxWidth: '400px',
                  maxHeight: '150px',
                  objectFit: 'contain',
                }}
              />
            )}
            <input
              ref={urlInputRef}
              value={imageUrl}
              onChange={(e) => handleURLentry(e)}
              type="text"
              className="w-72 p-3 outline-1 outline-[#E5E8EB] font-sans h-15 flex items-center rounded-xl outline-none"
              placeholder="Enter direct image URL (PNG/JPEG only)"
              readOnly={urlSubmitted}
            />
            <p className="text-xs text-gray-500 mt-2 max-w-72 text-center">
              Tip: Use the direct image URL, not a webpage URL. Right-click the
              image and select "Copy image address".
            </p>
            <div className="flex justify-center">
              {!urlSubmitted && (
                <button
                  className="bg-[#00d090] text-white py-2 px-4 rounded mt-5 mr-4"
                  onClick={handleUrlSubmit}
                >
                  Submit
                </button>
              )}
              <button
                className="bg-red-500 text-white py-2 px-4 rounded mt-5"
                onClick={handleRemoveOrCancel}
              >
                {urlSubmitted ? 'Remove' : 'Cancel'}
              </button>
            </div>
          </div>
        ) : (
          <label className="text-lg font-semibold text-center cursor-pointer">
            <div className="h-full flex flex-col justify-center items-center">
              <UploadIcon />
              <span className="mt-4">
                Click or drag file to this area to upload
              </span>
              <ul className="flex flex-col items-start text-xs text-gray-500 mt-3 list-disc list-inside">
                <li>The image upload should be in JPEG/PNG format.</li>
                <li>
                  You can crop and adjust the image to any size and aspect
                  ratio.
                </li>
              </ul>
              <button
                className="bg-[#00d090] text-white py-2 px-4 rounded-lg mt-4"
                onClick={handleUploadButtonClick}
              >
                Upload Image
              </button>
              <button
                className="bg-[#00d090] text-white py-2 px-4 rounded-lg mt-4"
                onClick={handleUploadAsUrlClick}
              >
                Upload as URL
              </button>
            </div>
          </label>
        )}
      </div>

      <Modal
        title="Crop Image"
        open={cropModalOpen}
        onCancel={handleCropCancel}
        footer={[
          <Button key="cancel" onClick={handleCropCancel}>
            Cancel
          </Button>,
          <Button key="finish" type="primary" onClick={handleCropFinish}>
            Finish
          </Button>,
        ]}
        width={700}
        maskClosable={false}
      >
        <div className="space-y-4">
          <div
            className="relative"
            style={{
              maxHeight: '500px',
              minHeight: '200px',
              background: '#f0f0f0',
            }}
          >
            {imageToCrop && (
              <Cropper
                ref={cropperRef}
                src={imageToCrop}
                style={{ height: 450, width: '100%' }}
                viewMode={1}
                background={false}
                responsive={true}
                autoCropArea={0.9}
                guides={true}
                dragMode="move"
                ready={() => {
                  const cropper = cropperRef.current?.cropper;
                  if (!cropper) return;

                  try {
                    cropper.reset();
                    const container = cropper.getContainerData();
                    const boxW = container.width * 0.9;
                    const boxH = container.height * 0.9;
                    cropper.setCropBoxData({
                      width: boxW,
                      height: boxH,
                      left: (container.width - boxW) / 2,
                      top: (container.height - boxH) / 2,
                    });
                  } catch (e) {
                    // If anything fails, fall back to cropper defaults.
                  }

                  // Capture the initial fitted zoom so 0% matches the default view.
                  const imageData = cropper.getImageData();
                  if (imageData?.naturalWidth) {
                    const fittedRatio =
                      imageData.width / imageData.naturalWidth;
                    baseZoomRef.current = fittedRatio || 1;
                  } else {
                    baseZoomRef.current = 1;
                  }

                  setIsCropperReady(true);
                }}
                aspectRatio={
                  aspectRatio === null || aspectRatio === undefined
                    ? NaN
                    : aspectRatio
                }
              />
            )}
          </div>

          <div className="space-y-3">
            <div className="flex items-center">
              <span className="mr-2 font-medium">Aspect Ratio:</span>
              <Radio.Group
                value={aspectRatio}
                onChange={(e) => {
                  handleAspectRatioChange(e.target.value);
                }}
                className="flex flex-wrap gap-2"
              >
                <Radio.Button value={null}>Free</Radio.Button>
                <Radio.Button value={1}>Square (1:1)</Radio.Button>
                <Radio.Button value={3 / 2}>Landscape (3:2)</Radio.Button>
                <Radio.Button value={4 / 1}>Wide (4:1)</Radio.Button>
                <Radio.Button value={2 / 3}>Portrait (2:3)</Radio.Button>
              </Radio.Group>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">Rotation:</span>
                <span className="text-sm text-gray-600">{rotation}°</span>
              </div>
              <Slider
                min={-180}
                max={180}
                step={1}
                value={rotation}
                onChange={setRotation}
                tooltip={{ formatter: (value) => `${value}°` }}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">Zoom:</span>
                <span className="text-sm text-gray-600">{zoom}%</span>
              </div>
              <Slider
                min={-50}
                max={200}
                step={1}
                value={zoom}
                onChange={setZoom}
                tooltip={{ formatter: (value) => `${value}%` }}
              />
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default UploadImages;
